import {
  Box,
  Archive,
  Calculator,
  Calendar,
  ChevronDownIcon,
  DollarSign,
  Package,
  Settings,
  HelpCircle,
  Shield,
  Truck,
  Users,
  MessageSquare,
  Rocket,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import { useSidebar } from "~/providers/sidebar.provider";
import { useAuth } from "~/store";
import { Role } from "~/utils/constants";
import { useFeedbackStore } from "~/store/feedback.store"
import { FeedbackModal } from "~/components/common/Feedback";


 // Add this import

type NavItem = {
  name?: string;
  icon?: React.ReactNode;
  path?: string;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
  gap?: boolean;
};

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const { user } = useAuth();
  const location = useLocation();
  const { openModal } = useFeedbackStore(); // Add this hook

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: "main" | "others";
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {}
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Create navItems based on user role
  const navItems = useMemo(
    () => [
      {
        name: "Bookings",
        icon: <Calendar size={20} />,
        path: "/bookings",
      },
      {
        name: "Clients",
        icon: <Users size={20} />,
        path: "/client/list",
      },
      {
        name: "Inventory",
        icon: <Package size={20} />,
        path: "/client/inventory",
      },
      {
        name: "AHC Storage",
        icon: <Archive size={20} />,
        path: "/ahc-storage",
      },
      { gap: true }, // Gap after AHC Storage
      {
        name: "Dispatch Tools",                                                       
        icon: <Truck size={20} />,
        subItems: [
          { name: "Summaries", path: "/dispatch/summaries" },
          {
            name: "Load Lists",
            path: "/dispatch/load-lists",
          },
          {
            name: "Quick Add Staff/Inventory",
            path: "/dispatch/quick-add",
          },
          {
            name: "Inventory Control",
            path: "/dispatch/inventory-control",
          },
          {
            name: "Internal Events/Tasks",
            path: "/dispatch/internal-events",
          },
          {
            name: "Print Labels",
            path: "/dispatch/print-labels",
          },
        ],
      },
      {
        name: "Mission Control",
        icon: <Rocket size={20} />,
        path: "/operations/mission-control",
      },
      { gap: true }, // Gap after Mission Control
      {
        name: "Operations",
        icon: <Settings size={20} />,
        subItems: [
          { name: "Staffing", path: "/operations/staffing" },
          {
            name: "Vessels + Shuttles",
            path: "/operations/vessels-and-shuttles",
          },
          {
            name: "Supply and Materials",
            path: "/operations/supplies",
          },
          {
            name: "Team + Client Notifications",
            path: "/notifications",
          },
          ...(user?.role === Role.SUPER_ADMIN
            ? [{ name: "Backend Setup", path: "/operations/backend-setup" }]
            : []),
          { name: "Service Metrics", path: "/operations/service-metrics" },
        ],
      },
      {
        name: "QB Connect (Pending)",
        icon: <DollarSign size={20} />,
        subItems: [
          { name: "Dashboard", path: "/billing/dashboard" },
          { name: "Invoicing", path: "/billing/invoicing" },
          { name: "Storage Invoicing", path: "/billing/storage" },
          { name: "Sales", path: "/billing/sales" },
          {
            name: "Expenses Management",
            path: "/billing/expenses",
          },
          { name: "Payroll", path: "/billing/payroll" },
        ],
      },
      {
        name: "Help & Support",
        icon: <HelpCircle size={20} />,
        path: "/help-resources",
      },
    ],
    []
  );

  // Admin items with spacing
  const adminItems = useMemo(
    () => [
      ...(user?.role === Role.SUPER_ADMIN
        ? [
            {
              name: "Admin Management",
              icon: <Shield size={20} />,
              path: "/admin-management",
            },
          ]
        : []),
    ],
    [user?.role]
  );

  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname]
  );

  useEffect(() => {
    let submenuMatched = false;
    navItems.forEach((nav, index) => {
      if (nav.subItems) {
        nav.subItems.forEach((subItem) => {
          if (isActive(subItem.path)) {
            setOpenSubmenu({
              type: "main",
              index,
            });
            submenuMatched = true;
          }
        });
      }
    });

    // Check admin items too (if they have subItems)
    adminItems.forEach((nav, index) => {
      if ('subItems' in nav && nav.subItems) {
        nav.subItems.forEach((subItem) => {
          if (isActive(subItem.path)) {
            setOpenSubmenu({
              type: "others",
              index,
            });
            submenuMatched = true;
          }
        });
      }
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location, isActive, navItems, adminItems]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number, menuType: "main" | "others") => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  const renderMenuItems = (items: NavItem[], menuType: "main" | "others") => (
    <ul className="flex flex-col gap-4">
      {items.map((nav, index) => {
        // Handle divider items
        if ('gap' in nav && nav.gap) {
          return (
            <li key={`divider-${index}`} className="flex justify-center py-1">
              <div className="w-16 h-px bg-gray-300"></div>
            </li>
          );
        }

        return (
        <li key={nav.name}>
          {nav.subItems ? (
            <button
              onClick={() => handleSubmenuToggle(index, menuType)}
              className={`menu-item group ${
                openSubmenu?.type === menuType && openSubmenu?.index === index
                  ? "menu-item-active"
                  : "menu-item-inactive"
              } cursor-pointer ${
                !isExpanded && !isHovered
                  ? "lg:justify-center"
                  : "lg:justify-start"
              }`}
            >
              <span
                className={`menu-item-icon-size  ${
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? "menu-item-icon-active"
                    : "menu-item-icon-inactive"
                }`}
              >
                {nav.icon}
              </span>
              {(isExpanded || isHovered || isMobileOpen) && (
                <span className="menu-item-text">{nav.name}</span>
              )}
              {(isExpanded || isHovered || isMobileOpen) && (
                <ChevronDownIcon
                  className={`ml-auto w-5 h-5 transition-transform duration-200 ${
                    openSubmenu?.type === menuType &&
                    openSubmenu?.index === index
                      ? "rotate-180 text-brand-500"
                      : ""
                  }`}
                />
              )}
            </button>
          ) : (
            nav.path && (
              <Link
                to={nav.path}
                className={`menu-item group ${
                  isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
                }`}
              >
                <span
                  className={`menu-item-icon-size ${
                    isActive(nav.path)
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                  }`}
                >
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span className="menu-item-text">{nav.name}</span>
                )}
              </Link>
            )
          )}
          {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
            <div
              ref={(el) => {
                subMenuRefs.current[`${menuType}-${index}`] = el;
              }}
              className="overflow-hidden transition-all duration-300"
              style={{
                height:
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? `${subMenuHeight[`${menuType}-${index}`]}px`
                    : "0px",
              }}
            >

              
              <ul className="mt-2 space-y-1 ml-[35px]">
                {nav.subItems.map((subItem) => (
                  <li key={subItem.name}>
                    <Link
                      to={subItem.path}
                      className={`menu-dropdown-item ${
                        isActive(subItem.path)
                          ? "menu-dropdown-item-active"
                          : "menu-dropdown-item-inactive"
                      }`}
                    >
                      {subItem.name}
                      <span className="flex items-center gap-1 ml-auto">
                        {subItem.new && (
                          <span
                            className={`ml-auto ${
                              isActive(subItem.path)
                                ? "menu-dropdown-badge-active"
                                : "menu-dropdown-badge-inactive"
                            } menu-dropdown-badge`}
                          >
                            new
                          </span>
                        )}
                        {subItem.pro && (
                          <span
                            className={`ml-auto ${
                              isActive(subItem.path)
                                ? "menu-dropdown-badge-active"
                                : "menu-dropdown-badge-inactive"
                            } menu-dropdown-badge`}
                          >
                            pro
                          </span>
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
        );
      })}
    </ul>
  );

  return (
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 left-0 bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-900 h-screen transition-all duration-300 ease-in-out z-[60] border-r border-gray-300 shadow-lg
        ${
          isExpanded || isMobileOpen
            ? "w-[290px]"
            : isHovered
            ? "w-[290px]"
            : "w-[90px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`py-8 flex ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link to="/">
          {isExpanded || isHovered || isMobileOpen ? (
            <div className="flex items-center gap-2">
              <img className="size-6 " src="/logo.png" alt="Logo" />
              <span className="ml-3 text-md font-bold">
                 Art Handler Collective 
              </span>
            </div>
          ) : (
            <img className="size-6" src="/logo.png" alt="Logo" />
          )}
        </Link>
      </div>
      
      <div className="flex flex-col flex-1 overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className="mb-6">
          <div className="flex flex-col gap-4">
            <div>{renderMenuItems(navItems, "main")}</div>
            {adminItems.length > 0 && (
              <>
                <div className="my-4" /> {/* Spacing above admin section */}
                <div>{renderMenuItems(adminItems, "others")}</div>
              </>
            )}
          </div>
        </nav>
      </div>

      {/* Add Feedback Button at Bottom - Only for Super Admins */}
      {(user?.role === Role.SUPER_ADMIN || user?.role === Role.ADMIN) && (
        <div className="mt-auto pb-4 border-t border-gray-300 pt-4">
          <button
            onClick={openModal}
            className={`w-full flex items-center gap-3 px-3 py-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors ${
              !isExpanded && !isHovered && !isMobileOpen
                ? "justify-center"
                : "justify-start"
            }`}
          >
            <MessageSquare size={20} />
            {(isExpanded || isHovered || isMobileOpen) && (
              <span className="text-sm font-medium">App Feedback</span>
            )}
          </button>
        </div>
      )}

      {/* Add Feedback Modal */}
      <FeedbackModal />
    </aside>
  );
};

export default AppSidebar;